import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Stack,
  Text,
  Link,
  SimpleGrid,
  Heading,
  HStack,
  Icon,
  VStack,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaShieldAlt,
  FaLock,
  FaUserShield,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const location = useLocation();
  const [accordionIndex, setAccordionIndex] = useState<number>(-1);

  // Close accordion when navigation occurs
  useEffect(() => {
    setAccordionIndex(-1);
  }, [location]);

  // Mobile accordion view
  const renderMobileFooter = () => (
    <>
      {/* Company Info - Always visible */}
      <VStack align='start' spacing={3} mb={4}>
        <Heading size='sm' color='primary.400'>
          PLAYZURA
        </Heading>
        <Text fontSize='xs' color='gray.400'>
          {t('footer.description')}
        </Text>
        <HStack spacing={3}>
          <Link href='#' color='gray.400' _hover={{ color: 'primary.400' }}>
            <Icon as={FaFacebook} boxSize={4} />
          </Link>
          <Link href='#' color='gray.400' _hover={{ color: 'primary.400' }}>
            <Icon as={FaTwitter} boxSize={4} />
          </Link>
          <Link href='#' color='gray.400' _hover={{ color: 'primary.400' }}>
            <Icon as={FaInstagram} boxSize={4} />
          </Link>
          <Link href='#' color='gray.400' _hover={{ color: 'primary.400' }}>
            <Icon as={FaYoutube} boxSize={4} />
          </Link>
        </HStack>
      </VStack>

      {/* Collapsible sections */}
      <Accordion
        allowToggle
        index={accordionIndex}
        onChange={expandedIndex => setAccordionIndex(expandedIndex as number)}
      >
        <AccordionItem border='none'>
          <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
            <Heading size='xs' color='primary.400' flex='1' textAlign='left'>
              {t('footer.company')}
            </Heading>
            <AccordionIcon color='primary.400' />
          </AccordionButton>
          <AccordionPanel pb={4} px={0}>
            <Stack spacing={0.5}>
              <Link
                href='/'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('nav.home')}
              </Link>
              <Link
                href='/poker'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.pokerGames')}
              </Link>
              <Link
                href='/contact'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.contactSupport')}
              </Link>
              <Link
                href='#'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.tournaments')}
              </Link>
              <Link
                href='#'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.promotions')}
              </Link>
            </Stack>
          </AccordionPanel>
        </AccordionItem>

        {/* Support */}
        <AccordionItem border='none'>
          <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
            <Heading size='xs' color='primary.400' flex='1' textAlign='left'>
              {t('footer.support')}
            </Heading>
            <AccordionIcon color='primary.400' />
          </AccordionButton>
          <AccordionPanel pb={4} px={0}>
            <Stack spacing={0.5}>
              <Link
                href='#'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.helpCenter')}
              </Link>
              <Link
                href='#'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.contactSupport')}
              </Link>
              <Link
                href='#'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.responsibleGaming')}
              </Link>
              <Link
                href='#'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.selfExclusion')}
              </Link>
              <Link
                href='#'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.depositLimits')}
              </Link>
            </Stack>
          </AccordionPanel>
        </AccordionItem>

        {/* Legal & Security */}
        <AccordionItem border='none'>
          <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
            <Heading size='xs' color='primary.400' flex='1' textAlign='left'>
              {t('footer.legal')}
            </Heading>
            <AccordionIcon color='primary.400' />
          </AccordionButton>
          <AccordionPanel pb={4} px={0}>
            <Stack spacing={0.5}>
              <Link
                href='#'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.termsOfService')}
              </Link>
              <Link
                href='#'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.privacyPolicy')}
              </Link>
              <Link
                href='#'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.cookiePolicy')}
              </Link>
              <Link
                href='#'
                color='gray.400'
                _hover={{ color: 'white' }}
                fontSize='xs'
              >
                {t('footer.gamingLicense')}
              </Link>
            </Stack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
      <HStack spacing={4} mt={4} justify='center'>
        <HStack spacing={2} color='gray.400'>
          <Icon as={FaShieldAlt} boxSize={4} />
          <Text fontSize='xs'>{t('footer.sslSecured')}</Text>
        </HStack>
        <HStack spacing={2} color='gray.400'>
          <Icon as={FaLock} boxSize={4} />
          <Text fontSize='xs'>{t('footer.encryption')}</Text>
        </HStack>
      </HStack>
    </>
  );

  // Desktop grid view
  const renderDesktopFooter = () => (
    <SimpleGrid
      columns={{ base: 1, sm: 2, md: 4 }}
      spacing={{ base: 6, md: 8 }}
    >
      {/* Company Info */}
      <VStack align='start' spacing={4}>
        <Heading size={{ base: 'sm', md: 'md' }} color='primary.400'>
          PLAYZURA
        </Heading>
        <Text fontSize={{ base: 'xs', md: 'sm' }} color='gray.400'>
          {t('footer.description')}
        </Text>
        <HStack spacing={{ base: 3, md: 4 }}>
          <Link href='#' color='gray.400' _hover={{ color: 'primary.400' }}>
            <Icon as={FaFacebook} boxSize={{ base: 4, md: 5 }} />
          </Link>
          <Link href='#' color='gray.400' _hover={{ color: 'primary.400' }}>
            <Icon as={FaTwitter} boxSize={5} />
          </Link>
          <Link href='#' color='gray.400' _hover={{ color: 'primary.400' }}>
            <Icon as={FaInstagram} boxSize={5} />
          </Link>
          <Link href='#' color='gray.400' _hover={{ color: 'primary.400' }}>
            <Icon as={FaYoutube} boxSize={5} />
          </Link>
        </HStack>
      </VStack>

      {/* Quick Links */}
      <VStack align='start' spacing={4}>
        <Heading size={{ base: 'xs', md: 'sm' }} color='primary.400'>
          {t('footer.company')}
        </Heading>
        <Stack spacing={{ base: 1.5, md: 2 }}>
          <Link
            href='/'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('nav.home')}
          </Link>
          <Link
            href='/poker'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.pokerGames')}
          </Link>
          <Link
            href='/contact'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.contactSupport')}
          </Link>
          <Link
            href='#'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.tournaments')}
          </Link>
          <Link
            href='#'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.promotions')}
          </Link>
        </Stack>
      </VStack>

      {/* Support */}
      <VStack align='start' spacing={4}>
        <Heading size={{ base: 'xs', md: 'sm' }} color='primary.400'>
          {t('footer.support')}
        </Heading>
        <Stack spacing={{ base: 1.5, md: 2 }}>
          <Link
            href='#'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.helpCenter')}
          </Link>
          <Link
            href='#'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.contactSupport')}
          </Link>
          <Link
            href='#'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.responsibleGaming')}
          </Link>
          <Link
            href='#'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.selfExclusion')}
          </Link>
          <Link
            href='#'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.depositLimits')}
          </Link>
        </Stack>
      </VStack>

      {/* Legal & Security */}
      <VStack align='start' spacing={4}>
        <Heading size={{ base: 'xs', md: 'sm' }} color='primary.400'>
          {t('footer.legal')}
        </Heading>
        <Stack spacing={{ base: 1.5, md: 2 }}>
          <Link
            href='#'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.termsOfService')}
          </Link>
          <Link
            href='#'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.privacyPolicy')}
          </Link>
          <Link
            href='#'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.cookiePolicy')}
          </Link>
          <Link
            href='#'
            color='gray.400'
            _hover={{ color: 'white' }}
            fontSize={{ base: 'xs', md: 'sm' }}
          >
            {t('footer.gamingLicense')}
          </Link>
          <HStack spacing={2} color='gray.400'>
            <Icon as={FaShieldAlt} boxSize={4} />
            <Text fontSize='sm'>{t('footer.sslSecured')}</Text>
          </HStack>
          <HStack spacing={2} color='gray.400'>
            <Icon as={FaLock} boxSize={4} />
            <Text fontSize='sm'>{t('footer.encryption')}</Text>
          </HStack>
        </Stack>
      </VStack>
    </SimpleGrid>
  );

  return (
    <Box bg='gray.900' color='white' mt='auto'>
      <Container maxW='7xl' py={{ base: 5, md: 10 }}>
        {isMobile ? renderMobileFooter() : renderDesktopFooter()}

        <Divider my={{ base: 5, md: 8 }} borderColor='gray.700' />

        {/* Bottom Section */}
        <Stack
          direction={{ base: 'column', md: 'row' }}
          justify='space-between'
          align='center'
          spacing={{ base: 3, md: 4 }}
        >
          <Text fontSize={{ base: 'xs', md: 'sm' }} color='gray.400'>
            {t('footer.copyright')}
          </Text>

          <HStack
            spacing={{ base: 4, md: 6 }}
            flexWrap='wrap'
            justify={{ base: 'center', md: 'flex-end' }}
          >
            <Text fontSize={{ base: '2xs', md: 'xs' }} color='gray.500'>
              {t('footer.gamblingWarning')}
            </Text>
            <HStack spacing={2}>
              <Icon
                as={FaUserShield}
                boxSize={{ base: 3, md: 4 }}
                color='gray.400'
              />
              <Text fontSize={{ base: '2xs', md: 'xs' }} color='gray.400'>
                {t('footer.ageRestriction')}
              </Text>
            </HStack>
          </HStack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
